import { Box, styled, Tooltip } from '@mui/material';
import { vec2 } from 'gl-matrix';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { ImageSlicePositions } from '~/containers/viewer/viewport/Viewport';
import { domainEventBus, RoiSelectionRequestedEvent } from '~/domain/events';
import { ToolManager } from '~/engine/tool/ToolManager';
import {
    environmentState,
    secondaryEnvironmentState,
} from '~/state/atoms/engineAtoms';
import {
    currentImageSliceOrientationPositionsState,
    lastMousePositionState,
    sideBySideImageSliceOrientationPositionsState,
} from '~/state/atoms/viewerAtoms';
import { PlaneView, PlaneViewType } from '../../../engine/view/views/PlaneView';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import useViewportSecondaryEnvironment from '~/hooks/useViewportSecondaryEnvironment';
import useViewportFocus from '~/hooks/useViewportFocus';
import useUpdateCursorRelatedStates from '~/hooks/viewer/useUpdateCursorRelatedStates';
import { selectedToolState } from '~/state/atoms/toolsAtoms';

const PlaneViewWebGLCanvas = styled('canvas')`
    width: 100%;
    height: 100%;
    z-index: 2;
    position: relative;
`;
const PlaneView2DCanvas = styled('canvas')`
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 5;
    background: transparent;
    pointer-events: none;
`;

interface PlaneViewFrameProps {
    view: PlaneView | null;
    canvas: React.RefObject<HTMLCanvasElement | null>;
    canvas2D: React.RefObject<HTMLCanvasElement | null>;
}

const PlaneViewFrame = ({ view, canvas, canvas2D }: PlaneViewFrameProps) => {
    const environment = useRecoilValue(environmentState);
    const secondaryEnvironment = useRecoilValue(secondaryEnvironmentState);
    const selectedTool = useRecoilValue(selectedToolState);
    const { focusOnStructure } = useViewportFocus();

    const env =
        view?.type === PlaneViewType.Primary
            ? environment
            : secondaryEnvironment;

    const { getEnvImagePositions } = useViewportEnvironment();
    const { getSecEnvImagePositions } = useViewportSecondaryEnvironment();
    const updateCursorRelatedStates = useUpdateCursorRelatedStates(env);

    const getNewCurrentImageSlice =
        view?.type === PlaneViewType.Primary
            ? getEnvImagePositions
            : getSecEnvImagePositions;

    const [
        currentImageSliceOrientationPositions,
        setCurrentImageSliceOrientationPositions,
    ] = useRecoilState(currentImageSliceOrientationPositionsState);
    const [
        sideBySideImageSliceOrientationPositions,
        setSideBySideImageSliceOrientationPositions,
    ] = useRecoilState(sideBySideImageSliceOrientationPositionsState);

    const [contourTooltip, setContourTooltip] = useState<string | undefined>(
        undefined
    );
    const [isMousePressed, setIsMousePressed] = useState<boolean>(false);
    const setLastMousePosition = useSetRecoilState(lastMousePositionState);

    useEffect(() => {
        const handleMousePress = () => {
            setIsMousePressed(true);
        };
        const handleMouseRelease = () => {
            setIsMousePressed(false);
        };
        const eventEmitter = ToolManager.getEventEmitter();
        eventEmitter.on('onMousePress', handleMousePress);
        eventEmitter.on('onMouseRelease', handleMouseRelease);
        return () => {
            eventEmitter.off('onMousePress', handleMousePress);
            eventEmitter.off('onMouseRelease', handleMouseRelease);
        };
    }, []);

    const pendingMouseRef = useRef<{
        clientX: number;
        clientY: number;
    } | null>(null);
    const timerIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const flushMouseUpdate = useCallback(() => {
        timerIdRef.current = null;
        const pending = pendingMouseRef.current;
        if (!pending) {
            return;
        }
        pendingMouseRef.current = null;

        setLastMousePosition({
            clientX: pending.clientX,
            clientY: pending.clientY,
        });

        if (env && view) {
            const patientCoords = view.clientToPatientCoordinates(
                vec2.fromValues(pending.clientX, pending.clientY)
            );
            updateCursorRelatedStates(patientCoords);
            setContourTooltip(
                isMousePressed
                    ? undefined
                    : env.structureSet?.findRoi(patientCoords)?.name
            );
        }
    }, [
        env,
        view,
        isMousePressed,
        setLastMousePosition,
        updateCursorRelatedStates,
    ]);

    // Keep a ref to the latest flush so the timer always
    // sees current env/view/isMousePressed values
    const flushRef = useRef(flushMouseUpdate);
    flushRef.current = flushMouseUpdate;

    // Clean up pending timer on unmount
    useEffect(() => {
        return () => {
            if (timerIdRef.current !== null) {
                clearTimeout(timerIdRef.current);
            }
        };
    }, []);

    const handleMouseMovement = useCallback(
        (mouseEvent: React.MouseEvent<HTMLCanvasElement>): void => {
            pendingMouseRef.current = {
                clientX: mouseEvent.clientX,
                clientY: mouseEvent.clientY,
            };
            if (timerIdRef.current === null) {
                timerIdRef.current = setTimeout(() => flushRef.current(), 150);
            }
        },
        []
    );

    const onWheel = (event: React.WheelEvent<HTMLCanvasElement>): void => {
        const currentImageSlicePostions =
            view?.type === PlaneViewType.Secondary
                ? sideBySideImageSliceOrientationPositions
                : currentImageSliceOrientationPositions;
        const newCurrentImageSlice: ImageSlicePositions =
            getNewCurrentImageSlice(currentImageSlicePostions);
        if (view?.type === PlaneViewType.Secondary) {
            setSideBySideImageSliceOrientationPositions(newCurrentImageSlice);
        } else {
            setCurrentImageSliceOrientationPositions(newCurrentImageSlice);
        }

        if (env && view) {
            const patientCoords = view.clientToPatientCoordinates(
                vec2.fromValues(event.clientX, event.clientY)
            );
            updateCursorRelatedStates(patientCoords);
        }
    };

    const handleRoiSelection = async (
        event: React.MouseEvent<HTMLCanvasElement>,
        shouldFocus: boolean
    ): Promise<void> => {
        if (!environment || !view || !environment.structureSet) {
            return;
        }
        if (selectedTool !== 'pan') {
            return;
        }
        if (view.consumeClickSuppression()) {
            return;
        }
        const selectedRoiAtCoords = view.checkForRoiAtCoords(
            vec2.fromValues(event.clientX, event.clientY)
        );
        if (
            selectedRoiAtCoords &&
            typeof selectedRoiAtCoords.roiNumber === 'number'
        ) {
            try {
                await domainEventBus.publish(
                    new RoiSelectionRequestedEvent(
                        selectedRoiAtCoords.roiNumber,
                        'user'
                    )
                );
                if (shouldFocus) {
                    focusOnStructure(selectedRoiAtCoords.roiNumber);
                }
            } catch (error) {
                console.error('Failed to handle ROI selection request', error);
            }
        }
    };

    const handleClick = (event: React.MouseEvent<HTMLCanvasElement>): void => {
        handleRoiSelection(event, false);
    };

    const handleDoubleClick = (
        event: React.MouseEvent<HTMLCanvasElement>
    ): void => {
        handleRoiSelection(event, true);
    };

    return (
        <Tooltip
            title={contourTooltip}
            followCursor
            disableInteractive
            placement="top"
            TransitionProps={{ timeout: 0 }}
        >
            <Box
                style={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                }}
            >
                <PlaneViewWebGLCanvas
                    ref={canvas as React.RefObject<HTMLCanvasElement>}
                    onMouseMove={handleMouseMovement}
                    onWheel={onWheel}
                    onClick={handleClick}
                    onDoubleClick={handleDoubleClick}
                />
                <PlaneView2DCanvas
                    ref={canvas2D as React.RefObject<HTMLCanvasElement>}
                />
            </Box>
        </Tooltip>
    );
};

export default PlaneViewFrame;
